import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { UserRole } from '../api/schema';
import type { Brand } from '../api/types';

import {
  adminNavForRole,
  isNavItemActive,
  primaryNavForRole,
  settingsNav,
  SETTINGS_PREFIX,
  showsTenantNav,
  type NavItem,
} from './navigation';

export interface SidebarProps {
  /** Documents waiting in the review queue; 0 hides the badge. */
  reviewCount: number;
  /** Whether the dead-letter queue needs attention. */
  deadLetterAlert: boolean;
  /** Current role — gates the ADMIN group (shown only to admin roles). */
  role: UserRole | null;
  /** The hostname-resolved brand — names and colours the mark, same as the sign-in card. */
  brand: Brand;
  /** The brand's logo, fetched and object-URL'd — null shows the initial-letter mark instead. */
  logoUrl: string | null;
  /** Whether a desktop build is published — gates the *Desktop uploader* row. */
  hasDesktopUploader?: boolean;
}

const navClass = (active: boolean): string => (active ? 'nav-item on' : 'nav-item');

/** The 216px left navigation from the Docsight design. */
export function Sidebar({
  reviewCount,
  deadLetterAlert,
  role,
  brand,
  logoUrl,
  hasDesktopUploader = false,
}: SidebarProps): React.JSX.Element {
  const { pathname } = useLocation();
  const mark = (brand.product_name.trim()[0] ?? 'D').toUpperCase();
  // A tenant role sees the tenant sections; a platform role (system/partner
  // admin) has no tenant, so it sees only its own admin items.
  const tenantNav = showsTenantNav(role);
  const primaryItems = primaryNavForRole(role);
  const adminItems = adminNavForRole(role);
  const onSettingsRoute = isNavItemActive(pathname, SETTINGS_PREFIX);
  const [settingsOpen, setSettingsOpen] = useState(onSettingsRoute);

  useEffect(() => {
    if (onSettingsRoute) setSettingsOpen(true);
  }, [onSettingsRoute]);

  const renderItem = (item: NavItem): React.JSX.Element => {
    const active = isNavItemActive(pathname, item.activePrefix);
    const isSettings = item.activePrefix === SETTINGS_PREFIX;
    return (
      <li key={item.to}>
        <Link
          to={item.to}
          className={navClass(active)}
          {...(active ? { 'aria-current': 'page' as const } : {})}
          {...(isSettings
            ? { 'aria-expanded': settingsOpen, 'aria-controls': 'settings-subnav' }
            : {})}
          {...(isSettings
            ? {
                onClick: () => {
                  setSettingsOpen((open) => !open);
                },
              }
            : {})}
        >
          <item.Icon />
          <span className="nav-label">{item.label}</span>
          {item.to === '/review' && reviewCount > 0 ? (
            <span className="nav-badge">
              {reviewCount}
              <span className="sr-only"> awaiting review</span>
            </span>
          ) : null}
          {item.to === '/admin/health' && deadLetterAlert ? (
            <span className="nav-dot">
              <span className="sr-only">attention needed</span>
            </span>
          ) : null}
        </Link>
      </li>
    );
  };

  return (
    <nav className="sidebar" aria-label="Primary">
      <div className="brand">
        {logoUrl != null ? (
          <img className="brand-mark brand-mark--logo" src={logoUrl} alt="" aria-hidden="true" />
        ) : (
          <span className="brand-mark" aria-hidden="true">
            {mark}
          </span>
        )}
        <span className="brand-name">{brand.product_name}</span>
      </div>

      {tenantNav ? <ul className="nav-group">{primaryItems.map(renderItem)}</ul> : null}

      {tenantNav && settingsOpen ? (
        <ul className="nav-subgroup" id="settings-subnav">
          {settingsNav(hasDesktopUploader).map((item) => {
            const active = pathname === item.to;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={active ? 'nav-subitem on' : 'nav-subitem'}
                  {...(active ? { 'aria-current': 'page' as const } : {})}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}

      {adminItems.length > 0 ? (
        <>
          <div className="nav-heading">ADMIN</div>
          <ul className="nav-group">{adminItems.map(renderItem)}</ul>
        </>
      ) : null}

      <div className="sidebar-footer">
        <p className="powered-by">Powered by Askelie · an ABC service</p>
      </div>
    </nav>
  );
}
